import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { DashboardRange, DashboardSummary } from '@upme/contracts';
import { Skeleton } from '@upme/ui/components/skeleton';
import { useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { dashboardSummaryOptions } from '../queries';
import { formatDayLabel, formatDurationMs, formatHourLabel, formatInt, formatPct, formatUsd } from './format';
import { SectionCard, StatCard } from './stat-card';

const RANGES: Array<{ value: DashboardRange; label: string }> = [
  { value: '7d', label: '7 дн' },
  { value: '30d', label: '30 дн' },
  { value: '90d', label: '90 дн' },
];

export function DashboardContent() {
  const [range, setRange] = useState<DashboardRange>('7d');
  const { data, isPending, isError, error } = useQuery(dashboardSummaryOptions(range));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="inline-flex rounded-md border bg-card p-1 text-sm">
          {RANGES.map((r) => (
            <button
              type="button"
              key={r.value}
              onClick={() => setRange(r.value)}
              className={`px-3 py-1 rounded ${
                range === r.value
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
        {data && (
          <div className="text-xs text-muted-foreground">
            обновлено {new Date(data.generatedAt).toLocaleTimeString('ru-RU')}
          </div>
        )}
      </div>

      {isError && (
        <div className="rounded-md border border-red-300 bg-red-50 dark:bg-red-950/20 p-4 text-sm text-red-700 dark:text-red-300">
          Ошибка загрузки дашборда: {(error as Error).message}
        </div>
      )}

      {isPending && <DashboardSkeleton />}
      {data && <DashboardSections data={data} />}
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-24 w-full" />
        ))}
      </div>
      <Skeleton className="h-64 w-full" />
      <Skeleton className="h-64 w-full" />
    </div>
  );
}

function DashboardSections({ data }: { data: DashboardSummary }) {
  return (
    <>
      <TopRow data={data} />
      <ModerationRow data={data} />
      <LlmRow data={data} />
      <ChannelsRow data={data} />
      <EventsRow data={data} />
      <AudienceRow data={data} />
      <ActivityRow data={data} />
    </>
  );
}

function TopRow({ data }: { data: DashboardSummary }) {
  // «Очередь модерации» = только EXTRACTED: посты, реально ждущие ручного решения
  // (ровно то, что показывает /import-queue по умолчанию). PENDING (ждут авто-
  // извлечения) намеренно НЕ суммируем — раньше это раздувало число мёртвыми
  // NEWS-постами без пути вперёд. Показываем PENDING отдельной подписью.
  const queue = data.moderation.extractedCount;
  const pending = data.moderation.pendingCount;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <StatCard
        label="Очередь модерации"
        value={formatInt(queue)}
        hint={pending > 0 ? `+${formatInt(pending)} ждут извлечения` : 'все извлечено'}
        tone={queue > 50 ? 'warning' : 'default'}
      />
      <StatCard
        label="Старейший PENDING"
        value={formatDurationMs(data.moderation.oldestPendingAgeMs)}
        hint={data.moderation.pendingCount > 0 ? 'требует разбора' : 'очередь пуста'}
        tone={
          data.moderation.oldestPendingAgeMs && data.moderation.oldestPendingAgeMs > 24 * 3600 * 1000
            ? 'danger'
            : 'default'
        }
      />
      <StatCard
        label="События PUBLISHED"
        value={formatInt(data.events.byStatus.published)}
        hint={`${formatPct(data.events.importedSharePct, 0)} из импорта`}
      />
      <StatCard
        label="LLM-расход за период"
        value={formatUsd(data.llm.totalCostUsd)}
        hint={`${formatInt(data.llm.totalInputTokens + data.llm.totalOutputTokens)} токенов`}
      />
    </div>
  );
}

function ModerationRow({ data }: { data: DashboardSummary }) {
  const funnelRows = [
    { name: 'PENDING', value: data.moderation.funnel.pending, color: '#94a3b8' },
    { name: 'EXTRACTED', value: data.moderation.funnel.extracted, color: '#3b82f6' },
    { name: 'IMPORTED', value: data.moderation.funnel.imported, color: '#10b981' },
    { name: 'IGNORED', value: data.moderation.funnel.ignored, color: '#a3a3a3' },
    { name: 'REJECTED', value: data.moderation.funnel.rejected, color: '#ef4444' },
  ];
  const hourly = data.moderation.incomingPerHour24h.map((p) => ({
    hour: p.hour,
    label: formatHourLabel(p.hour),
    count: p.count,
  }));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <SectionCard title="Воронка модерации" subtitle="за выбранный период">
        <div className="h-60">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={funnelRows}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="value">
                {funnelRows.map((row) => (
                  <Cell key={row.name} fill={row.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </SectionCard>

      <SectionCard title="Входящие посты за 24 часа" subtitle="по часам">
        <div className="h-60">
          {hourly.length === 0 ? (
            <EmptyState label="Нет входящих за последние 24ч" />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={hourly}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Line type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </SectionCard>
    </div>
  );
}

function LlmRow({ data }: { data: DashboardSummary }) {
  const costPerDay = data.llm.costPerDay.map((p) => ({
    label: formatDayLabel(p.date),
    cost: p.cost,
  }));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <SectionCard title="LLM-стоимость по дням" className="lg:col-span-2">
        <div className="h-56">
          {costPerDay.length === 0 ? (
            <EmptyState label="Нет LLM-вызовов за период" />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={costPerDay}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v}`} />
                <Tooltip formatter={(v) => (typeof v === 'number' ? formatUsd(v) : String(v))} />
                <Line type="monotone" dataKey="cost" stroke="#10b981" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="grid grid-cols-3 gap-2 text-sm pt-2 border-t">
          <KvCell label="Всего" value={formatUsd(data.llm.totalCostUsd)} />
          <KvCell label="Средняя/msg" value={formatUsd(data.llm.avgCostPerMessage)} />
          <KvCell label="Токены" value={formatInt(data.llm.totalInputTokens + data.llm.totalOutputTokens)} />
        </div>
      </SectionCard>

      <SectionCard title="По моделям">
        {data.llm.byModel.length === 0 ? (
          <EmptyState label="Нет данных" />
        ) : (
          <ul className="space-y-2 text-sm">
            {data.llm.byModel.map((m) => (
              <li key={m.model} className="flex flex-col gap-0.5">
                <div className="flex justify-between items-baseline">
                  <span className="font-mono text-xs truncate" title={m.model}>
                    {m.model}
                  </span>
                  <span className="tabular-nums">{formatUsd(m.costUsd)}</span>
                </div>
                <div className="text-xs text-muted-foreground">
                  {formatInt(m.calls)} вызовов · {formatInt(m.inputTokens + m.outputTokens)} токенов
                </div>
              </li>
            ))}
          </ul>
        )}
      </SectionCard>
    </div>
  );
}

function ChannelsRow({ data }: { data: DashboardSummary }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <SectionCard
        title={`Каналы с ошибками (${data.channels.erroring.length})`}
        subtitle={`всего активных: ${data.channels.active} · неактивных: ${data.channels.inactive}`}
      >
        {data.channels.erroring.length === 0 ? (
          <EmptyState label="Все каналы работают штатно" />
        ) : (
          <ul className="space-y-2 text-sm">
            {data.channels.erroring.map((c) => (
              <li key={c.id} className="flex items-center justify-between gap-2">
                <Link
                  to="/source-channels/$channelId"
                  params={{ channelId: c.id }}
                  className="truncate hover:underline"
                  title={c.lastError ?? ''}
                >
                  {c.title}
                </Link>
                <span className="text-red-600 dark:text-red-400 text-xs tabular-nums">
                  {c.consecutiveErrors} ошибок подряд
                </span>
              </li>
            ))}
          </ul>
        )}
      </SectionCard>

      <SectionCard title="Топ-каналов по импорту" subtitle="за выбранный период">
        {data.channels.topByImports.length === 0 ? (
          <EmptyState label="Нет IMPORTED за период" />
        ) : (
          <ul className="space-y-2 text-sm">
            {data.channels.topByImports.map((c) => (
              <li key={c.id} className="flex items-center justify-between gap-2">
                <Link
                  to="/source-channels/$channelId"
                  params={{ channelId: c.id }}
                  className="truncate hover:underline"
                >
                  {c.title}
                </Link>
                <span className="tabular-nums">{formatInt(c.importedCount)}</span>
              </li>
            ))}
          </ul>
        )}
      </SectionCard>
    </div>
  );
}

function EventsRow({ data }: { data: DashboardSummary }) {
  const upcoming = data.events.upcomingByDay.map((p) => ({ label: formatDayLabel(p.date), count: p.count }));
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <SectionCard title="Предстоящие события" subtitle="ближайшие 30 дней" className="lg:col-span-2">
        <div className="h-56">
          {upcoming.length === 0 ? (
            <EmptyState label="Нет событий на ближайшие 30 дней" />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={upcoming}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#3b82f6" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </SectionCard>

      <SectionCard title="По городам" subtitle="upcoming 30 дней">
        {data.events.upcomingByCity.length === 0 ? (
          <EmptyState label="Нет данных" />
        ) : (
          <ul className="space-y-1 text-sm">
            {data.events.upcomingByCity.map((c) => (
              <li key={c.cityId} className="flex justify-between">
                <span className="truncate">{c.cityName}</span>
                <span className="tabular-nums">{formatInt(c.count)}</span>
              </li>
            ))}
          </ul>
        )}
      </SectionCard>

      <SectionCard title="Топ-категорий" subtitle="за период" className="lg:col-span-3">
        {data.events.topCategories.length === 0 ? (
          <EmptyState label="Нет событий за период" />
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-sm">
            {data.events.topCategories.map((c) => (
              <div key={c.slug} className="rounded border p-2">
                <div className="font-medium truncate">{c.name}</div>
                <div className="text-xs text-muted-foreground tabular-nums">{formatInt(c.count)} событий</div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
}

function AudienceRow({ data }: { data: DashboardSummary }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <SectionCard title="Аудитория">
        <div className="grid grid-cols-2 gap-2">
          <KvCell label="Всего" value={formatInt(data.audience.totalUsers)} />
          <KvCell label={`Новых за период`} value={formatInt(data.audience.newUsersInRange)} />
          <KvCell label="DAU" value={formatInt(data.audience.dau)} />
          <KvCell label="WAU" value={formatInt(data.audience.wau)} />
          <KvCell label="MAU" value={formatInt(data.audience.mau)} />
          <KvCell label="Premium" value={formatPct(data.audience.premiumSharePct, 1)} />
        </div>
      </SectionCard>

      <SectionCard title="По городам">
        {data.audience.byCity.length === 0 ? (
          <EmptyState label="Нет данных" />
        ) : (
          <ul className="space-y-1 text-sm">
            {data.audience.byCity.map((c) => (
              <li key={c.cityId} className="flex justify-between">
                <span className="truncate">{c.cityName}</span>
                <span className="tabular-nums">{formatInt(c.count)}</span>
              </li>
            ))}
          </ul>
        )}
      </SectionCard>

      <SectionCard title="Топ-события по посещаемости">
        {data.audience.topEvents.length === 0 ? (
          <EmptyState label="Нет посещений" />
        ) : (
          <ul className="space-y-1 text-sm">
            {data.audience.topEvents.map((e) => (
              <li key={e.id} className="flex justify-between gap-2">
                <Link
                  to="/events/$eventId"
                  params={{ eventId: e.id }}
                  className="truncate hover:underline"
                  title={`${e.title} · ${e.cityName}`}
                >
                  {e.title}
                </Link>
                <span className="tabular-nums">{formatInt(e.attendanceCount)}</span>
              </li>
            ))}
          </ul>
        )}
      </SectionCard>
    </div>
  );
}

const ACTIVITY_SERIES: Array<{
  key: keyof DashboardSummary['activity']['totals'];
  name: string;
  color: string;
}> = [
  { key: 'events', name: 'События', color: '#3b82f6' },
  { key: 'participations', name: 'Участия', color: '#10b981' },
  { key: 'photos', name: 'Фото', color: '#f59e0b' },
  { key: 'photoTags', name: 'Отметки', color: '#a855f7' },
  { key: 'messages', name: 'Сообщения', color: '#ef4444' },
];

function ActivityRow({ data }: { data: DashboardSummary }) {
  const { totals, daily } = data.activity;
  const rows = daily.map((d) => ({ ...d, label: formatDayLabel(d.date) }));
  // daily всегда непустой (бэкенд zero-fill'ит дни) — EmptyState показываем по факту
  // отсутствия активности за период, а не по длине ряда.
  const hasData = Object.values(totals).some((v) => v > 0);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatCard label="Регистрации" value={formatInt(totals.registrations)} hint="живые юзеры за период" />
        <StatCard label="События от юзеров" value={formatInt(totals.events)} hint="создано живыми" />
        <StatCard label="Участия" value={formatInt(totals.participations)} hint="нажатий «Участвую»" />
        <StatCard label="Фото" value={formatInt(totals.photos)} hint="загружено живыми" />
        <StatCard label="Отметки" value={formatInt(totals.photoTags)} hint="на фото" />
        <StatCard label="Личные сообщения" value={formatInt(totals.messages)} hint="DIRECT-чаты" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SectionCard title="Регистрации по дням" subtitle="живые юзеры за период">
          <div className="h-60">
            {!hasData ? (
              <EmptyState label="Нет данных за период" />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={rows}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="registrations"
                    stroke="#6366f1"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </SectionCard>

        <SectionCard title="Активность по дням" subtitle="события · участия · фото · отметки · сообщения">
          <div className="h-60">
            {!hasData ? (
              <EmptyState label="Нет данных за период" />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={rows}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {ACTIVITY_SERIES.map((s) => (
                    <Line
                      key={s.key}
                      type="monotone"
                      dataKey={s.key}
                      name={s.name}
                      stroke={s.color}
                      strokeWidth={2}
                      dot={false}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </SectionCard>
      </div>
    </div>
  );
}

function KvCell({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function EmptyState({ label }: { label: string }) {
  return <div className="h-full flex items-center justify-center text-sm text-muted-foreground">{label}</div>;
}
